import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, Tabs, Tab
} from '@mui/material';
import {
  Work as WorkIcon, Person as PersonIcon, Check as CheckIcon,
  Close as CloseIcon, Business as BusinessIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const JobApplicationsAdmin = () => {
  const queryClient = useQueryClient();
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const { data: applications, isLoading, error } = useQuery(
    'adminJobApplications',
    async () => {
      const response = await apiClient.get('/api/admin/job-applications');
      return response.data;
    },
    {
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Admin Job Applications API Error:', error);
      }
    }
  );

  const adminActionMutation = useMutation(
    async ({ applicationId, action, adminNotes }) => {
      const response = await apiClient.post(`/api/admin/job-applications/${applicationId}/review`, {
        action,
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminJobApplications');
        setDialogOpen(false);
        setSelectedApplication(null);
        setAdminNotes('');
      },
      onError: (error) => {
        console.error('Admin action failed:', error);
        alert('Failed to process admin action: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleAdminAction = (action) => {
    if (selectedApplication) {
      adminActionMutation.mutate({
        applicationId: selectedApplication.id,
        action,
        adminNotes
      });
    }
  };

  const openDialog = (application) => {
    setSelectedApplication(application);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading job applications...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load job applications: {error.message}
        </Alert>
      </Box>
    );
  }

  const allApplications = applications?.applications || [];
  const pendingApplications = allApplications.filter(app => app.status === 'pending');
  const approvedApplications = allApplications.filter(app => app.status === 'approved');
  const rejectedApplications = allApplications.filter(app => app.status === 'rejected');

  const displayApplications = tabValue === 0 ? pendingApplications : 
                              tabValue === 1 ? approvedApplications : 
                              rejectedApplications;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{
        color: '#FFFFFF',
        fontWeight: 700,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <WorkIcon sx={{ color: '#F7931A' }} />
        Job Applications Management
      </Typography>

      <Typography variant="body1" sx={{ color: '#6C7293', mb: 3 }}>
        Review and manage job applications submitted by users.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: '#2B3139', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: '#B7BDC6',
              '&.Mui-selected': {
                color: '#F7931A'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#F7931A'
            }
          }}
        >
          <Tab label={`Pending (${pendingApplications.length})`} />
          <Tab label={`Approved (${approvedApplications.length})`} />
          <Tab label={`Rejected (${rejectedApplications.length})`} />
        </Tabs>
      </Box>

      {displayApplications.length === 0 ? (
        <Card sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <WorkIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
              No {tabValue === 0 ? 'Pending' : tabValue === 1 ? 'Approved' : 'Rejected'} Applications
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293' }}>
              {tabValue === 0 ? 'All applications have been reviewed.' : 
               tabValue === 1 ? 'No applications have been approved yet.' :
               'No applications have been rejected yet.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {displayApplications.map((application) => (
            <ListItem
              key={application.id}
              sx={{
                border: '1px solid #2B3139',
                borderRadius: 1,
                mb: 2,
                background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
                '&:hover': {
                  borderColor: '#F7931A'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#F7931A' }}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                      {application.applicant?.name || application.applicant?.email}
                    </Typography>
                    <Chip
                      label={application.job?.job_title}
                      size="small"
                      sx={{ background: 'rgba(247, 147, 26, 0.2)', color: '#F7931A' }}
                    />
                    <Chip
                      label={application.job?.company_name}
                      size="small"
                      sx={{ background: 'rgba(2, 192, 118, 0.2)', color: '#02C076' }}
                    />
                    <Chip
                      label={application.status}
                      size="small"
                      color={
                        application.status === 'approved' ? 'success' :
                        application.status === 'rejected' ? 'error' : 'warning'
                      }
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Applied for:</strong> {application.job?.job_title} at {application.job?.company_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Cover Letter:</strong> {application.cover_letter?.substring(0, 100)}...
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Applied:</strong> {new Date(application.created_at).toLocaleString()}
                    </Typography>
                    {application.admin_notes && (
                      <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                        <strong>Admin Notes:</strong> {application.admin_notes}
                      </Typography>
                    )}
                  </Box>
                }
              />

              {application.status === 'pending' && (
                <Button
                  variant="contained"
                  onClick={() => openDialog(application)}
                  sx={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                    }
                  }}
                >
                  Review
                </Button>
              )}
            </ListItem>
          ))}
        </List>
      )}

      {/* Admin Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#1a1a1a' }}>
          Review Job Application
        </DialogTitle>
        <DialogContent sx={{ background: '#1a1a1a', color: '#FFFFFF' }}>
          {selectedApplication && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: '#F7931A' }}>
                {selectedApplication.job?.job_title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Company:</strong> {selectedApplication.job?.company_name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Applicant:</strong> {selectedApplication.applicant?.name || selectedApplication.applicant?.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Applicant Email:</strong> {selectedApplication.applicant?.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Applied:</strong> {new Date(selectedApplication.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Cover Letter:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#B7BDC6', whiteSpace: 'pre-wrap', p: 2, bgcolor: '#0B0E11', borderRadius: 1 }}>
                {selectedApplication.cover_letter}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes (Optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#6C7293' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                  },
                  '& .MuiInputLabel-root': { color: '#6C7293' },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#1a1a1a', p: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#6C7293' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleAdminAction('rejected')}
            variant="outlined"
            color="error"
            disabled={adminActionMutation.isLoading}
            sx={{
              borderColor: '#F84960',
              color: '#F84960',
              '&:hover': {
                borderColor: '#DC2626',
                backgroundColor: 'rgba(248, 73, 96, 0.1)'
              }
            }}
          >
            Reject
          </Button>
          <Button
            onClick={() => handleAdminAction('approved')}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              }
            }}
            disabled={adminActionMutation.isLoading}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobApplicationsAdmin;

